import { ErrorInfo } from "@bootstrap";
import { useEffect, useState } from "react";
import { Navigate, useRouteError } from "react-router";
// Guarded accessor, NOT @posthog/react's usePostHog: this boundary is in the
// entry chunk, and @posthog/react statically imports posthog-js — importing it
// here would pull the SDK back into the entry bundle (SD-10530). Null until
// the lazily-loaded SDK initializes; pre-init captures were dropped anyway
// (uninitialized + opted-out default), so skipping them is behavior-identical.
import { getPostHog } from "@/analytics/posthog";
import { PageContent } from "@/components/PageContent/PageContent";
import { safeConsole } from "@/utils/safeConsole";
import { captureClientRouteError } from "@/utils/sentryRouteError";
import { checkAuth } from "./authLoader";
import { ChunkUpdatingNotice } from "./ChunkUpdatingNotice";
import {
  isChunkLoadError,
  isReloadThrottled,
  recoverFromChunkLoadError,
} from "./chunkLoadRecovery";
import {
  CHUNK_RECOVERY_EVENT,
  collectChunkRecoveryProps,
} from "./chunkRecoveryTelemetry";

/**
 * Error boundary that renders inside the AuthenticatedShell.
 * Shell stays mounted so the user keeps the nav bar and can navigate away.
 *
 * If the error occurred because the session expired (token missing/invalid),
 * redirect to login instead of showing a scary error page.
 *
 * If the error is a stale code-split chunk (older tab vs. a newer deploy),
 * reload onto the current asset graph instead of showing an error — the
 * navigation has already committed to the target URL, so the reload lands the
 * user on the page they clicked.
 *
 * For auth-loader failures (outside the shell), AuthErrorBoundary handles them instead.
 */
export function RouteErrorBoundary() {
  const error = useRouteError() as Error;
  const message = error instanceof Error ? error.message : String(error);
  const chunkError = isChunkLoadError(message);
  // True once recovery is exhausted (a reload was already triggered within the
  // throttle window, or the effect's reload attempt was throttled) — fall back
  // to the real error instead of a stuck "updating" state. Seeded from the
  // pure throttle check so an already-exhausted chunk error renders the real
  // error immediately instead of flashing "Updating…" for one frame.
  const [recoveryExhausted, setRecoveryExhausted] = useState(
    () => chunkError && isReloadThrottled(),
  );

  useEffect(() => {
    if (!checkAuth()) {
      return;
    }
    if (chunkError) {
      // Expected post-deploy condition, not a bug — recover, don't report the
      // exception. Do measure it: send_instantly because a successful recovery
      // has already queued the reload, so the event must leave via beacon now.
      const recovered = recoverFromChunkLoadError();
      const recoveryProps = collectChunkRecoveryProps(
        "route",
        recovered ? "reload" : "exhausted",
        message,
      );
      getPostHog()?.capture(CHUNK_RECOVERY_EVENT, recoveryProps, {
        send_instantly: true,
      });
      if (!recovered) {
        setRecoveryExhausted(true);
        captureClientRouteError(error, recoveryProps);
        // Recovery exhausted = a real stuck-user error, not transient deploy
        // churn. Report to PostHog too for Sentry/PostHog parity.
        getPostHog()?.captureException(error);
      }
      return;
    }
    safeConsole.error("error-boundary", "RouteErrorBoundary caught:", error);
    captureClientRouteError(error);
    getPostHog()?.captureException(error);
  }, [error, chunkError, message]);

  if (!checkAuth()) {
    return <Navigate to="/login" replace />;
  }

  if (chunkError && !recoveryExhausted) {
    return <ChunkUpdatingNotice />;
  }

  return (
    <PageContent>
      <ErrorInfo
        title="Uh-oh!"
        subtitle="Something went wrong"
        description="We are working to fix this issue"
      />
    </PageContent>
  );
}
